<template>
    <div
        class="fixed inset-0 w-full h-full bg-black/50 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        data-testid="image-viewer-backdrop"
        ref="backdrop"
        @click.self="emit('close')"
        @keydown.esc="emit('close')"
    >
        <div class="absolute top-4 right-4 flex gap-3">
            <button
                class="text-white hover:text-gray-300 p-2"
                :aria-label="t('general.download')"
                data-testid="download-button"
                @click="downloadAttachment(attachment)"
            >
                <activix-spinner
                    class="h-5 w-5"
                    data-testid="download-spinner"
                    v-if="isDownloading(attachment)"
                />
                <icon
                    name="regular/download-button"
                    class="w-5 h-5"
                    data-testid="download-icon"
                    v-else
                />
            </button>

            <button
                class="text-white hover:text-gray-300 p-2"
                :aria-label="t('general.close')"
                data-testid="close-button"
                @click="emit('close')"
            >
                <icon name="regular/remove" class="w-6 h-6" />
            </button>
        </div>

        <activix-spinner
            class="h-10 w-10 text-white"
            data-testid="image-loading-spinner"
            v-if="!imageBlobUrl && !imageError"
        />
        <img
            :src="imageBlobUrl"
            :alt="attachment.filename"
            class="max-w-[90vw] max-h-[90vh] object-contain"
            data-testid="fullscreen-image"
            v-else-if="imageBlobUrl"
        />
        <span class="text-white text-sm" data-testid="image-error" v-else>
            {{ t('smsCommunication.messagesList.failedToDownloadAttachment') }}
        </span>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import type { MessageAttachment } from '@/entities/types/ConversationType.ts';
    import { useAttachmentDownload } from '@/hooks/useAttachmentDownload.ts';
    import ActivixSpinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';

    interface Props {
        attachment: MessageAttachment;
    }

    const props = defineProps<Props>();

    const emit = defineEmits<(e: 'close') => void>();

    const { t } = useI18n();
    const { downloadAttachment, fetchAttachmentBlobUrl, isDownloading } = useAttachmentDownload();

    const backdrop = ref<HTMLElement | null>(null);
    const imageBlobUrl = ref<string | null>(null);
    const imageError = ref<boolean>(false);

    onMounted(async () => {
        backdrop.value?.focus();

        try {
            imageBlobUrl.value = await fetchAttachmentBlobUrl(props.attachment);
        } catch {
            imageError.value = true;
        }
    });
</script>
